import { useEffect } from 'react';
import { Box, Button, Grid, Typography, Card, CardContent, MenuItem, Divider } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Save, ArrowLeft } from 'lucide-react';
import { PageHeader } from '@/components/navigation/PageHeader';
import { FormField } from '@/components/forms/FormField';
import { tokens } from '@/theme/tokens';
import { useSuperAdminSchoolStore } from '@/store/superAdmin/schoolStore';
import { useOrganizationStore } from '@/store/superAdmin/organizationStore';
import { INDIAN_STATES } from '@/constants/states';

const validationSchema = Yup.object({
  name: Yup.string().required('School name is required'),
  organizationId: Yup.string().required('Organization is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  phone: Yup.string().required('Phone is required').matches(/^\d{10}$/, 'Phone must be exactly 10 digits'),
  website: Yup.string().url('Invalid URL'),
  address: Yup.string().required('Address is required'),
  city: Yup.string().trim().required('City is required').matches(/^[a-zA-Z\s.,'-]+$/, 'City must contain only letters'),
  state: Yup.string().trim().required('State is required').matches(/^[a-zA-Z\s.,'-]+$/, 'State must contain only letters'),
  country: Yup.string().trim().required('Country is required').matches(/^[a-zA-Z\s.,'-]+$/, 'Country must contain only letters'),
  postalCode: Yup.string().required('Postal Code is required').matches(/^\d{6}$/, 'Postal code must be exactly 6 digits'),
  schoolCode: Yup.string().required('School code is required'),
  timezone: Yup.string().required('Timezone is required'),
  currency: Yup.string().required('Currency is required'),
  principalName: Yup.string().trim().matches(/^[a-zA-Z\s'-]+$/, 'Principal name must contain only letters').when('$isEditMode', {
    is: false,
    then: () => Yup.string().trim().required('Principal name is required').matches(/^[a-zA-Z\s'-]+$/, 'Principal name must contain only letters')
  }),
  principalEmail: Yup.string().email('Invalid email').when('$isEditMode', {
    is: false,
    then: () => Yup.string().required('Principal email is required')
  }),
  principalPhone: Yup.string().matches(/^\d{10}$/, 'Phone must be exactly 10 digits').when('$isEditMode', {
    is: false,
    then: () => Yup.string().required('Principal phone is required').matches(/^\d{10}$/, 'Phone must be exactly 10 digits')
  }),
});

export function CreateGlobalSchoolPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const [searchParams] = useSearchParams();
  const orgId = searchParams.get('orgId');
  const { createSchool, updateSchool, selectedSchool, fetchSchoolById } = useSuperAdminSchoolStore();
  const { organizations, fetchOrganizations } = useOrganizationStore();

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  useEffect(() => {
    if (isEditMode) {
      fetchSchoolById(id);
    }
  }, [id, isEditMode, fetchSchoolById]);

  const schoolData = isEditMode ? selectedSchool : null;

  const initialValues = {
    name: schoolData?.school_name || '',
    organizationId: schoolData?.organization_id || orgId || '',
    email: schoolData?.email || '',
    phone: schoolData?.phone || '',
    website: schoolData?.website || '',
    address: schoolData?.address || '',
    city: schoolData?.city || '',
    state: schoolData?.state || '',
    country: schoolData?.country || 'India',
    postalCode: schoolData?.postal_code || '',
    schoolCode: schoolData?.school_code || '',
    timezone: schoolData?.timezone || 'Asia/Kolkata',
    currency: schoolData?.currency || 'INR',
    status: schoolData?.status || 'ACTIVE',
    principalName: '',
    principalEmail: '',
    principalPhone: '',
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = {
        organization_id: values.organizationId,
        school_name: values.name,
        school_code: values.schoolCode,
        email: values.email,
        phone: values.phone,
        website: values.website,
        logo: "logo.png", // Mock logo since file upload not currently present
        address: values.address,
        city: values.city,
        state: values.state,
        country: values.country,
        postal_code: values.postalCode,
        timezone: values.timezone,
        currency: values.currency,
        status: values.status,
      };

      if (isEditMode) {
        await updateSchool(id, payload);
        toast.success('School updated successfully');
      } else {
        payload.principal_name = values.principalName;
        payload.principal_phone = values.principalPhone;
        payload.principal_email = values.principalEmail;
        await createSchool(payload);
        toast.success('School created successfully');
      }
      navigate('/super-admin/schools');
    } catch (error) {
      toast.error(error.message || 'Failed to create school');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ pb: 4 }}>
      <PageHeader
        title={isEditMode ? "Edit School" : "Create School"}
        subtitle={isEditMode ? "Update school details." : "Add a new school under an organization."}
        actions={
          <Button
            variant="outlined"
            startIcon={<ArrowLeft size={18} />}
            onClick={() => navigate('/super-admin/schools')}
            sx={{
              borderRadius: tokens.radius.sm,
              fontWeight: 600,
              textTransform: 'none',
              borderColor: tokens.colors.border,
              color: tokens.colors.text.primary,
            }}
          >
            Back to List
          </Button>
        }
      />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
        context={{ isEditMode }}
      >
        {({ isSubmitting }) => (
          <Form autoComplete="off">
            <Box sx={{ width: '100%' }}>
              <Card elevation={0} sx={{ borderRadius: tokens.radius.md, border: `1px solid ${tokens.colors.border}`, mb: 3 }}>
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 3, color: tokens.colors.primary.main }}>
                    School Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <FormField name="organizationId" label="Select Organization *" select>
                        <MenuItem value="">Select Organization</MenuItem>
                        {organizations.map(org => (
                          <MenuItem key={org.id} value={org.id}>{org.organization_name || org.name}</MenuItem>
                        ))}
                      </FormField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="name" label="School Name *" placeholder="e.g. Green Valley High School" />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="schoolCode" label="School Code *" placeholder="e.g. GVHS-01" />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="email" label="Contact Email *" type="email" />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="phone" label="Contact Phone *" />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <FormField name="website" label="Website URL" placeholder="https://" />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <FormField name="address" label="Street Address *" />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="city" label="City *" />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="state" label="State/Province *" select>
                        <MenuItem value=""><em>Select State</em></MenuItem>
                        {INDIAN_STATES.map((st) => (
                          <MenuItem key={st} value={st}>{st}</MenuItem>
                        ))}
                      </FormField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="country" label="Country *" />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="postalCode" label="Postal Code *" />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="timezone" label="Timezone *" />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="currency" label="Currency *" />
                    </Grid>
                    {isEditMode && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <FormField name="status" label="Status *" select>
                          <MenuItem value="ACTIVE">Active</MenuItem>
                          <MenuItem value="INACTIVE">Inactive</MenuItem>
                        </FormField>
                      </Grid>
                    )}
                  </Grid>

                  {!isEditMode && (
                    <>
                      <Divider sx={{ my: 4 }} />
                      <Typography variant="h6" fontWeight={600} sx={{ mb: 3, color: tokens.colors.primary.main }}>
                        Principal Information
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12 }}>
                          <FormField name="principalName" label="Principal Name *" />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <FormField name="principalEmail" label="Principal Email *" type="email" />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <FormField name="principalPhone" label="Principal Phone *" />
                        </Grid>
                      </Grid>
                    </>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                    <Button
                      variant="outlined"
                      onClick={() => navigate('/super-admin/schools')}
                      disabled={isSubmitting}
                      sx={{ borderRadius: tokens.radius.sm, px: 4, fontWeight: 600 }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isSubmitting}
                      startIcon={<Save size={18} />}
                      sx={{ borderRadius: tokens.radius.sm, px: 4, fontWeight: 600, boxShadow: 'none' }}
                    >
                      {isSubmitting ? 'Saving...' : 'Save School'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
}
